// Display helpers: initials, avatar colours, dates in French, message grouping.

const AVATAR_COLOURS = ['#0F766E', '#C2410C', '#7C3AED', '#2563EB', '#B45309', '#BE185D', '#15803D', '#4F46E5'];

export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? [words[0]![0], words[words.length - 1]![0]] : [name.trim().slice(0, 2)];
  return letters.join('').toUpperCase();
}

/** A stable colour per user, from their id. */
export function avatarColour(id: string): string {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return AVATAR_COLOURS[Math.abs(hash) % AVATAR_COLOURS.length]!;
}

const time = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' });
const fullDate = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'Europe/Paris',
});
const shortDate = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', timeZone: 'Europe/Paris' });
const dayKeyFormat = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: 'Europe/Paris',
});

export const formatTime = (iso: string) => time.format(new Date(iso));

/** Calendar day in Paris, e.g. 2026-11-04: messages are grouped and separated by it. */
export const dayKey = (iso: string | Date) => dayKeyFormat.format(new Date(iso));

/** « Aujourd'hui », « Hier », or « lundi 2 novembre 2026 ». */
export function dayLabel(iso: string): string {
  const key = dayKey(iso);
  const now = new Date();
  if (key === dayKey(now)) return "Aujourd'hui";
  if (key === dayKey(new Date(now.getTime() - 86_400_000))) return 'Hier';
  const label = fullDate.format(new Date(iso));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** For lists: the time today, « Hier », else a short date. */
export function relativeDay(iso: string): string {
  const key = dayKey(iso);
  const now = new Date();
  if (key === dayKey(now)) return formatTime(iso);
  if (key === dayKey(new Date(now.getTime() - 86_400_000))) return 'Hier';
  return shortDate.format(new Date(iso));
}

/** « 15:11 », « Hier 15:11 », « 3 nov. 15:11 »: a message's date in a list. */
export function dayAndTime(iso: string): string {
  const day = relativeDay(iso);
  const time = formatTime(iso);
  return day === time ? time : `${day} ${time}`;
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} Mo`;
}

/** Successive messages of one author within 5 minutes, the same day, are grouped. */
export const GROUP_WINDOW_MS = 5 * 60 * 1000;
